import { token } from '@atlaskit/tokens';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getSelectDatabaseDraft } from '@databases/redux/selectors/getSelectDatabaseDraft';
import { type DatabaseFieldType } from '@databases/types/database';
import { RECURSION_LIMIT } from '@databases/utils/constants';
import { getEntryDetailsTargetValues } from '@databases/utils/database/field-types/entryDetails';
import { getDatabaseEntryRequired } from '@databases/utils/database/utils';
import React, { type FunctionComponent } from 'react';
import styled from 'styled-components';

import { FieldValueView } from './FieldValueView';
import { type ViewComponentProps } from './types';

const Container = styled.ul`
  list-style-type: none;
  padding: 0;

  > li + li {
    margin-top: ${token('space.075', '6px')};
  }
`;

const EntryDetailsValueView: FunctionComponent<
  ViewComponentProps<DatabaseFieldType.ENTRY_DETAILS>
> = ({ contentId, field, entryId, recursionCount, ...props }) => {
  const targetValues = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true,
    });
    const entry = entryId ? getDatabaseEntryRequired(databaseDraft, entryId) : undefined;

    return getEntryDetailsTargetValues({ database: databaseDraft, field, entry }, state, {
      selectDatabase: getSelectDatabaseDraft(true),
    });
  });

  if (targetValues.length === 0 || recursionCount >= RECURSION_LIMIT) {
    return null;
  } else {
    return (
      <Container>
        {targetValues.map((targetValue, i) => (
          <li key={`${i}`}>
            <FieldValueView
              {...props}
              contentId={targetValue.database.contentId}
              entryId={targetValue.entry.id}
              field={targetValue.field}
              value={targetValue.value}
              recursionCount={recursionCount + 1}
            />
          </li>
        ))}
      </Container>
    );
  }
};

export default EntryDetailsValueView;
